// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import AddAllToList from "@bg/App/Components/Cart/AddAllToList";
import { CartPageContainerMessagesContext } from "@bg/App/Components/Cart/CartPageContainerMessagesContextWrapper";
import ContinueShoppingLink from "@bg/App/Components/ContinueShoppingLink";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { enablePostalCodeLocationFinder } from "@bg/App/Store/Selectors/PostalCodeSelector";
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import CartLineCardExpanded, { CartLineCardExpandedStyles } from "@bg/Overrides/Widgets/Cart/CartLineCardExpanded";
import CartSaveOrderButton from "@bg/Overrides/Widgets/Cart/CartSaveOrderButton";
import { constants } from "@bg/XDL/constants";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { hasLocalStorage, parseBoolean } from "@bg/XDL/Utilities";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Clickable from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showLineNotes = "showLineNotes",
    showImage = "showImage",
    showProductTitle = "showProductTitle",
    showBrand = "showBrand",
    showRemoveAllActions = "showRemoveAllActions",
    showPerUnitPrice = "showPerUnitPrice",
    showPriceLabel = "showPriceLabel",
    showAddToList = "showAddToList",
    showItemCount = "showItemCount",
    showProductNumber = "showProductNumber",
    showBackToTop = "showBackToTop",
    showVmiBinDetails = "showVmiBinDetails",
    customCSS = "customCSS",
    // Xng-Change
    hideAddToList = "hideAddToList",
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.description-grid-item{
}

.description-text{
}

.email-grid-item{
}

.email-text-field{
}

.email-button{
}

.disclaimer-grid-item{
}

.disclaimer-text{
}`;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showLineNotes]: boolean;
        [fields.showImage]: boolean;
        [fields.showProductTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showRemoveAllActions]: boolean;
        [fields.showPerUnitPrice]: boolean;
        [fields.showPriceLabel]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showItemCount]: boolean;
        [fields.showProductNumber]: boolean;
        [fields.showBackToTop]: boolean;
        [fields.showVmiBinDetails]: boolean;
        [fields.customCSS]: string;
        // Xng-Change
        [fields.hideAddToList]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    currentCart: getCurrentCartState(state).value,
    cartState: getCurrentCartState(state),
    promotionsDataView: getCurrentPromotionsDataView(state),
    settingsCollection: getSettingsCollection(state),
    isClearingCart: state.pages.cart.isClearingCart,
    isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
    isLoading: state.data.carts.isLoading,
    isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
    showPricing: state.context.showPricing,
    showInventory: state.context.showInventory,
    session: state.context.session,
    enablePostalCodeLocationFinder: enablePostalCodeLocationFinder(state),
});

const mapDispatchToProps = {
    clearCurrentCart,
    updateCartLine,
    removeCartLine,
};

interface ExternalProps {
    // cart passed down from other pages
    customCart?: any;
    editable?: boolean;
    showTitle?: boolean;
    customPropmotions?: any;
    hideQuotePricedProducts?: boolean;
}

type Props = ExternalProps &
    Partial<OwnProps> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLinesStyles {
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noCartLinesText?: TypographyProps;
    noCartLinesIcon?: IconPresentationProps;
    headerWrapper?: InjectableCss;
    header?: CartLinesHeaderStyles;
    cardList?: CardListStyles;
    cardContainer?: CardContainerStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    footerWrapper?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
}

interface CartLinesHeaderStyles {
    itemCountText?: TypographyProps;
    condensedViewCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    condensedViewCheckbox?: CheckboxPresentationProps;
    overflowMenu?: OverflowMenuPresentationProps;
    actionsContainer?: InjectableCss;
    saveOrderLink?: LinkPresentationProps;
    addAllToListLink?: LinkPresentationProps;
    removeAllLink?: LinkPresentationProps;
    mainSection?: InjectableCss;
    warningIcon?: IconPresentationProps;
    warningSection?: InjectableCss;
    warningText?: InjectableCss;
}

export const cartLinesStyles: CartLinesStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 300px;
            align-items: center;
        `,
    },
    noCartLinesText: {
        variant: "h3",
    },
    noCartLinesIcon: {
        src: "ShoppingCart",
        size: 45,
        color: "text.link",
        css: css`
            margin-bottom: 20px;
        `,
    },
    headerWrapper: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding-bottom: 10px;
        `,
    },
    header: {
        condensedViewCheckboxGroup: {
            css: css`
                margin-left: auto;
            `,
        },
        condensedViewCheckbox: {
            typographyProps: {
                css: css`
                    margin-left: 10px;
                `,
            },
        },
        actionsContainer: {
            css: css`
                display: flex;
                align-items: center;
                justify-content: space-between;
            `,
        },
        saveOrderLink: linkIconStyleGenerator({
            icon: "SystemUpdate",
            textSize: "15px",
            iconSize: 16,
            additionalTypographyCss: css`
                opacity: 0.8;
            `,
        }),
        addAllToListLink: linkIconStyleGenerator({
            icon: "AddToList",
            textSize: "15px",
            iconSize: 16,
            additionalTypographyCss: css`
                opacity: 0.8;
            `,
            wrapperCss: css`
                margin-left: 30px;
            `,
        }),
        removeAllLink: linkIconStyleGenerator({
            icon: "Delete",
            textSize: "15px",
            iconSize: 16,
            additionalTypographyCss: css`
                opacity: 0.8;
            `,
        }),
        mainSection: {
            css: css`
                display: flex;
                align-items: center;
                padding-bottom: 5px;
            `,
        },
        warningIcon: {
            src: "AlertCircle",
            color: "danger.main",
            css: css`
                margin-right: 10px;
            `,
        },
        warningSection: {
            css: css`
                display: flex;
                align-items: center;
                border: 2px solid ${getColor("danger.main")};
                padding: 15px 10px;
            `,
        },
    },
    footerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    backToTopButton: {
        variant: "secondary",
        css: css`
            margin: 16px 0;
        `,
    },
};

const StyledSection = getStyledWrapper("section");

const styles = cartLinesStyles;

const CartLines: FC<Props> = ({
    currentCart,
    cartState,
    promotionsDataView,
    settingsCollection,
    clearCurrentCart,
    fields,
    isClearingCart,
    isUpdatingCartLine,
    isLoading,
    updateCartLine,
    removeCartLine,
    editable,
    customCart,
    session,
    hideQuotePricedProducts,
    isRemovingCartLine,
    showPricing,
    showInventory,
    enablePostalCodeLocationFinder,
}) => {
    const [isRemovingAll, setIsRemovingAll] = React.useState(false);
    const messagesContext = React.useContext(CartPageContainerMessagesContext);

    const cart = customCart || currentCart;
    let cartLines = cart?.cartLines || cart?.quoteLineCollection;
    const productsCannotBePurchased = cartLines?.some((o: any) => o.isRestricted || !o.isActive || o.cantBuy);
    const { productSettings } = settingsCollection;
    const { value: promotions } = promotionsDataView;
    const isLoadingCart =
        isLoading[cart?.id] || (cart?.status === "AlternateCart" && isLoading[API_URL_CURRENT_FRAGMENT]);

    React.useEffect(() => {
        messagesContext.flushMessages();

        if (!isLoadingCart) {
            setTimeout(() => {
                if (cart?.cartLines && cart?.cartLines.length === 0) {
                    messagesContext.addMessage({
                        key: "cart_noCartLinesMessage",
                        type: "warning",
                        message: siteMessage("Cart_NoOrderLines"),
                    });
                }

                if (cart?.cartLines && cart?.cartLines.length > 0) {
                    messagesContext.flushMessages();
                    messagesContext.removeMessage("cart_noCartLinesMessage");
                }

                if (
                    enablePostalCodeLocationFinder &&
                    !hasLocalStorage(constants.localStorage.POSTAL_CODE_ID) &&
                    !session.isAuthenticated
                ) {
                    messagesContext.addMessage({
                        key: "cart_hasNoPostalCode",
                        type: "warning",
                        message: siteMessage("Cart_HasNoPostalCode"),
                    });
                }

                if (cart?.hasInsufficientInventory === true && !session.isGuest) {
                    messagesContext.addMessage({
                        key: "Custom_Cart_NoEnoughInventory",
                        type: "warning",
                        message: siteMessage("Custom_Cart_NoEnoughInventory"),
                    });
                }
                // if (cartState.value?.fulfillmentMethod !== FulfillmentMethod.PickUp) {
                //     if (cart?.properties?.hasPickUpOnlyItems === "true") {
                //         if (cart?.properties?.isLatitudeCustomer === "true") {
                //             messagesContext.addMessage({
                //                 key: "cart_LatitudeCustomer_HasPickupOnly",
                //                 type: "warning",
                //                 closeable: true,
                //                 message: siteMessage("Cart_HasPickupOnly_LatitudeCustomer"),
                //             });
                //         } else {
                //             messagesContext.addMessage({
                //                 key: "cart_HasPickupOnly",
                //                 type: "warning",
                //                 closeable: true,
                //                 message: siteMessage("Cart_HasPickupOnly"),
                //             });
                //         }
                //     }
                // }

                if (cart?.properties && cart?.properties?.erpSystemIsDown === "true") {
                    messagesContext.addMessage({
                        key: "cart_erpSystemIsDown",
                        type: "danger",
                        closeable: false,
                        message: siteMessage("Custom_erpSystemIsDown"),
                    });
                }

                if (cart?.properties && cart?.properties?.priceFailedForOneOrMoreLine === "true") {
                    messagesContext.addMessage({
                        key: "cart_priceFailedToLoad",
                        type: "danger",
                        closeable: false,
                        message: siteMessage("Custom_PriceFailedForOneOrMoreLines"),
                    });
                }

                if (cart?.properties && cart?.properties?.requestQuoteMsg && cart?.properties?.requestQuoteMsg.length) {
                    messagesContext.addMessage({
                        key: "cart_requestQuoteMsg",
                        type: "warning",
                        closeable: false,
                        message: cart?.properties?.requestQuoteMsg,
                    });
                }

                const isQuoteCart = cart?.type === "Quote";
                const hasNonMatchQty = cart?.cartLines?.some((d: any) => {
                    const exactQty = d.properties.exactQty ? +d.properties.exactQty : undefined;
                    const minimumQty = d.properties.minimumQty ? +d.properties.minimumQty : undefined;

                    return (
                        (exactQty !== undefined && d.qtyOrdered !== null && d.qtyOrdered !== exactQty) ||
                        (minimumQty !== undefined && d.qtyOrdered !== null && d.qtyOrdered < minimumQty)
                    );
                });
                const qtyCheckFailed = isQuoteCart && hasNonMatchQty;
                if (qtyCheckFailed) {
                    messagesContext.addMessage({
                        key: "cart_HasItemsWithInvalidQty",
                        type: "warning",
                        closeable: true,
                        message: siteMessage("Cart_HasItemsWithInvalidQty"),
                    });
                }

                const noValidCredit = cart?.properties.isValidCredit && cart?.properties.isValidCredit === "false";
                if (noValidCredit) {
                    messagesContext.addMessage({
                        key: "cart_HasNoValidCredit",
                        type: "warning",
                        closeable: true,
                        message: siteMessage("Cart_HasNoValidCredit"),
                    });
                }

                if (productsCannotBePurchased) {
                    messagesContext.addMessage({
                        key: "Cart_ProductsCannotBePurchased",
                        type: "danger",
                        closeable: false,
                        message: siteMessage("Cart_ProductsCannotBePurchased"),
                    });
                }

                if (cart?.cartNotPriced) {
                    messagesContext.addMessage({
                        key: "Cart_NoPriceAvailableAtCheckout",
                        type: "warning",
                        closeable: false,
                        message: siteMessage("Cart_NoPriceAvailableAtCheckout"),
                    });
                }
                if (cart?.cartNotPriced) {
                    messagesContext.addMessage({
                        key: "Cart_NoInventoryAvailableAtCheckout",
                        type: "warning",
                        closeable: false,
                        message: siteMessage("Cart_NoInventoryAvailableAtCheckout"),
                    });
                }
                /**
                 * XNG-Changes: BG-2900
                 */
                if (parseBoolean(cart?.properties.isCreditCardDefaultPayment)) {
                    messagesContext.addMessage({
                        key: "Custom_Cart_CreditCardDefaultPayment",
                        type: "danger",
                        closeable: false,
                        message: siteMessage("Custom_Cart_CreditCardDefaultPayment"),
                    });
                }
            });
        }
        return () => {
            messagesContext.flushMessages();
        };
    }, [cartState, customCart, cart, cart?.cartLines]);

    if (!cart || (cart.lineCount && !cart.cartLines) || isClearingCart) {
        return (
            <StyledSection {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledSection>
        );
    }

    const openConfirmRemoveAllModal = () => {
        setIsRemovingAll(true);
    };

    const removeAllClickHandler = () => {
        clearCurrentCart();
    };

    const getPickupOnlyLabel = (cartLine: any) => {
        if (cartLine.properties?.pickupOnly !== "true") {
            return undefined;
        }

        if (cartState.value?.fulfillmentMethod === FulfillmentMethod.PickUp) {
            return undefined;
        }

        if (cart.properties?.isLatitudeCustomer === "true") {
            return translate("Attention");
        }
        return translate("Pickup Only");
    };

    if (hideQuotePricedProducts && cart.status === "Cart") {
        cartLines = cart.cartLines.filter((cartline: { quoteRequired: any }) => !cartline.quoteRequired);
    }

    const cartLinesDisplay = cartLines.map((cartLine: any) => {
        if (!showPricing && cartLine.isPromotionItem) {
            return;
        }

        const matchingPromotions = promotions ? promotions.filter(promo => promo.orderLineId === cartLine.id) : [];
        const showRemoveAction = !cartLine.isPromotionItem && cart.canModifyOrder;
        const pickupOnlyLabel = getPickupOnlyLabel(cartLine);
        return (
            <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                <CartLineCardExpanded
                    cart={cart}
                    promotions={matchingPromotions}
                    productSettings={productSettings}
                    showInventoryAvailability={showInventory}
                    hideAddToList={fields?.hideAddToList}
                    showLineNotes={fields?.showLineNotes}
                    showImage={fields?.showImage}
                    showProductTitle={fields?.showProductTitle}
                    showBrand={fields?.showBrand}
                    showPerUnitPrice={fields?.showPerUnitPrice}
                    showPriceLabel={fields?.showPriceLabel}
                    showAddToList={fields?.showAddToList}
                    showProductNumber={fields?.showProductNumber}
                    extendedStyles={styles.cardExpanded}
                    updateCartLine={updateCartLine}
                    removeCartLine={removeCartLine}
                    showRemoveAction={showRemoveAction}
                    showVmiBinDetails={fields?.showVmiBinDetails}
                    editable={editable !== false}
                    pickupOnlyLabel={pickupOnlyLabel}
                />
            </CartLineContext.Provider>
        );
    });

    if (cartLines.length === 0 && !customCart) {
        return <></>;
    }

    const customCssWrapper = {
        css: css`
            ${fields?.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledSection {...styles.wrapper}>
                <LoadingOverlay
                    {...styles.loadingOverlay}
                    loading={
                        !customCart &&
                        (isLoadingCart ||
                            isUpdatingCartLine ||
                            promotionsDataView.isLoading ||
                            cartState.isLoading ||
                            isRemovingCartLine)
                    }
                >
                    {!customCart && (
                        <CartLinesHeader
                            totalItemCount={cartLines!.length}
                            productsCannotBePurchased={productsCannotBePurchased}
                            cartNotPriced={cart.cartNotPriced}
                            onRemoveAllClick={openConfirmRemoveAllModal}
                            showItemCount={fields?.showItemCount}
                            showRemoveAllActions={fields?.showRemoveAllActions}
                        />
                    )}
                    <LinesHeader editable={editable !== false} />
                    {cartLinesDisplay}
                    {fields?.showBackToTop !== false && <CartLinesFooter />}
                </LoadingOverlay>
                <TwoButtonModal
                    modalIsOpen={isRemovingAll}
                    headlineText={translate("Remove All Items")}
                    messageText={siteMessage("Custom_Cart_RemoveAllProductsConfirmationMsg")}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Remove All")}
                    onCancel={() => setIsRemovingAll(false)}
                    onSubmit={removeAllClickHandler}
                    submitTestSelector="myCartRemovingAll"
                />
            </StyledSection>
        </StyledWrapper>
    );
};

export const LinesHeader = ({ editable }: { editable: boolean }) => (
    <StyledWrapper
        css={css`
            display: flex;
            background: ${getColor("common.ui4")};
            padding: 10px;
        `}
        id="cartHeader"
    >
        <Typography
            size={fontConfig.fontSize.xSmall}
            lineHeight={fontConfig.lineHeight.small}
            weight={fontConfig.weight.semiBold}
            transform="uppercase"
            css={css`
                width: calc(100% - 395px);
                letter-spacing: 0.2px;
                @media (max-width: 900px) {
                    width: 100%;
                }
            `}
        >
            {translate("Products")}
        </Typography>
        <StyledWrapper
            css={css`
                display: flex;
                flex-grow: 1;
                padding-right: ${editable ? "25px" : "0"};
                @media (max-width: 900px) {
                    display: none;
                }

                @media print {
                    display: none;
                }
            `}
        >
            <Typography
                size={fontConfig.fontSize.xSmall}
                lineHeight={fontConfig.lineHeight.small}
                weight={fontConfig.weight.semiBold}
                transform="uppercase"
                css={css`
                    width: calc(100% / 3);
                    letter-spacing: 0.2px;
                `}
            >
                {translate("Price/Unit")}
            </Typography>
            <Typography
                size={fontConfig.fontSize.xSmall}
                lineHeight={fontConfig.lineHeight.small}
                weight={fontConfig.weight.semiBold}
                transform="uppercase"
                css={css`
                    text-align: center;
                    width: calc(100% / 3);
                    letter-spacing: 0.2px;
                `}
            >
                {translate("QTY")}
            </Typography>
            <Typography
                size={fontConfig.fontSize.xSmall}
                lineHeight={fontConfig.lineHeight.small}
                weight={fontConfig.weight.semiBold}
                transform="uppercase"
                css={css`
                    text-align: right;
                    width: calc(100% / 3);
                    letter-spacing: 0.2px;
                `}
            >
                {translate("Total")}
            </Typography>
        </StyledWrapper>
    </StyledWrapper>
);

interface CartLinesHeaderProps {
    totalItemCount: number;
    productsCannotBePurchased?: boolean;
    cartNotPriced?: boolean;
    onRemoveAllClick: () => void;
    showItemCount?: boolean;
    showRemoveAllActions?: boolean;
}

const CartLinesHeader: FC<CartLinesHeaderProps> = ({
    totalItemCount,
    productsCannotBePurchased,
    cartNotPriced,
    onRemoveAllClick,
    showItemCount,
    showRemoveAllActions,
}) => {
    const headerStyles = styles.header || {};

    return (
        <StyledSection {...styles.headerWrapper} id="cartHeader">
            <StyledSection {...headerStyles.mainSection}>
                <Hidden
                    css={css`
                        width: 100%;
                    `}
                    above="xs"
                >
                    <StyledWrapper {...headerStyles.actionsContainer}>
                        <ContinueShoppingLink above="sm" />
                        <OverflowMenu position="end" {...headerStyles.overflowMenu}>
                            <CartSaveOrderButton variant="clickable" extendedStyles={headerStyles.saveOrderLink} />
                            <AddAllToList variant="clickable" />
                            {showRemoveAllActions && (
                                <Clickable onClick={onRemoveAllClick}>{translate("Remove All")}</Clickable>
                            )}
                        </OverflowMenu>
                    </StyledWrapper>
                </Hidden>
                <Hidden
                    below="sm"
                    css={css`
                        width: 100%;
                    `}
                >
                    <StyledWrapper {...headerStyles.actionsContainer}>
                        <StyledWrapper>
                            <CartSaveOrderButton variant="link" extendedStyles={headerStyles.saveOrderLink} />
                            <AddAllToList variant="link" extendedStyles={headerStyles.addAllToListLink} />
                        </StyledWrapper>
                        {showRemoveAllActions && (
                            <Link
                                onClick={onRemoveAllClick}
                                {...headerStyles.removeAllLink}
                                data-test-selector="cartlineHeader_removeAll"
                            >
                                {translate("Remove All")}
                            </Link>
                        )}
                    </StyledWrapper>
                </Hidden>
            </StyledSection>
        </StyledSection>
    );
};

const CartLinesComponent: FC<Props> = ({
    customCart,
    fields,
    showTitle,
    editable,
    customPropmotions,
    hideQuotePricedProducts,
    ...otherProps
}) => {
    return (
        <CartLines
            customCart={customCart}
            customPropmotions={customPropmotions}
            showTitle={showTitle}
            fields={fields}
            editable={editable}
            hideQuotePricedProducts={hideQuotePricedProducts}
            {...otherProps}
        />
    );
};

export const CartComponent = connect(mapStateToProps, mapDispatchToProps)(CartLinesComponent);

const CartLinesFooter: FC = () => {
    const backToTopClickHandler = () => {
        const header = document.getElementById("cartHeader");
        if (header) {
            window.scrollTo(0, header.offsetTop);
        }
    };

    return (
        <StyledSection {...styles.footerWrapper}>
            <Button onClick={backToTopClickHandler} {...styles.backToTopButton}>
                {translate("Back to Top")}
            </Button>
        </StyledSection>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartLines),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.showLineNotes,
                displayName: "Show Line Notes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                tooltip: "This setting does not apply to Punchout orders, which will never display Line Notes.",
                tab: basicTab,
                sortOrder: 0,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 1,
            },
            {
                name: fields.showProductTitle,
                displayName: "Product title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 3,
            },
            {
                name: fields.showRemoveAllActions,
                displayName: "Remove all actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 4,
            },
            {
                name: fields.showPerUnitPrice,
                displayName: "Per unit price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 5,
            },
            {
                name: fields.showPriceLabel,
                displayName: "Price label",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 6,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to list",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 7,
            },
            {
                name: fields.showItemCount,
                displayName: "Item count",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 8,
            },
            {
                name: fields.showProductNumber,
                displayName: "Product number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 9,
            },
            {
                name: fields.showBackToTop,
                displayName: "Back to top",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 10,
            },
            {
                name: fields.showVmiBinDetails,
                displayName: "VMI Bin details",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 11,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
